import React, { useState, useEffect, useCallback } from "react";
import {
	createSession,
	readValues,
	setValue,
	keepSessionAlive,
} from "../functions/excelFunctions";
import {
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	FormHelperText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
	ComposedChart,
	Line,
	XAxis,
	YAxis,
	Scatter,
	ResponsiveContainer,
	Label,
	Tooltip,
} from "recharts";
import "../css/RiskFactorAnalysis.css";
import {
	percentageSorting,
	floatStringSorting,
} from "../functions/sortingFunctions";

function RiskFactorAnalysis() {
	const [sessionInfo, setSessionInfo] = useState(null);
	const [tableTitle, setTableTitle] = useState([]);
	const [chartTitle, setChartTitle] = useState("");
	const [tableHeaders, setTableHeaders] = useState([]);
	const [tableRows, setTableRows] = useState([]);
	const [datagridColumns, setDatagridColumns] = useState([]);
	const [datagridRows, setDatagridRows] = useState([]);
	const [chartData, setChartData] = useState([]);
	const [linesData, setLinesData] = useState([]);
	const [chart, setChart] = useState("");
	const sheet = "Risk factor analysis";
	const functionCallDelay = 100; //ms
	const riskFactoroptions = [
		"Domestic abuse",
		"Parental mental health",
		"Deprivation",
	];

	const [inputFilled, setInputFilled] = useState(new Array(4).fill(false));

	const percentages = [-30, -20, -10, 0, 10, 20, 30];

	const getData = useCallback(() => {
		if (sessionInfo) {
			setTimeout(() => {
				readValues(sessionInfo, setTableTitle, sheet, "K10:K10");
			}, 1 * functionCallDelay);

			setTimeout(() => {
				readValues(sessionInfo, setTableHeaders, sheet, "K11:N11");
			}, 2 * functionCallDelay);

			setTimeout(() => {
				readValues(sessionInfo, setTableRows, sheet, "K12:N33");
			}, 3 * functionCallDelay);

			setTimeout(() => {
				readValues(
					sessionInfo,
					setChartData,
					"Regression (scatter)",
					"N5:O111"
				);
			}, 4 * functionCallDelay);

			setTimeout(() => {
				readValues(
					sessionInfo,
					setLinesData,
					"Regression (scatter)",
					"S19:V38"
				);
			}, 5 * functionCallDelay);
		}
	}, [
		sessionInfo,
		setTableHeaders,
		setTableRows,
		setChartData,
		setTableTitle,
	]);

	useEffect(() => {
		createSession(setSessionInfo);
	}, []);

	useEffect(() => {
		getData();
	}, [getData]);

	useEffect(() => {
		tableHeaders.length &&
			tableRows.length &&
			setDatagridColumns([
				{
					field: "columnA",
					headerName: tableHeaders[0][0],
					minWidth: 200,
					flex: 1,
				},
				{
					field: "columnB",
					headerName: tableHeaders[0][1],
					width: 200,
					sortComparator: floatStringSorting,
				},
				{
					field: "columnC",
					headerName: tableHeaders[0][2],
					width: 200,
					sortComparator: floatStringSorting,
				},
				{
					field: "columnD",
					headerName: tableHeaders[0][3],
					width: 100,
					sortComparator: percentageSorting,
				},
			]);

		const currentDatagridRows = [];
		tableRows.forEach((row, index) => {
			currentDatagridRows.push({
				id: index,
				columnA: row[0],
				columnB: row[1],
				columnC: row[2],
				columnD: row[3],
			});
		});
		setDatagridRows(currentDatagridRows);
	}, [tableHeaders, tableRows]);

	const calculateDataForTrendline = (data) => {
		const n = data.length;
		const xSum = data.reduce((acc, d) => acc + d.index, 0);
		const ySum = data.reduce((acc, d) => acc + d.scatterY, 0);
		const xySum = data.reduce((acc, d) => acc + d.index * d.scatterY, 0);
		const xSquaredSum = data.reduce((acc, d) => acc + d.index ** 2, 0);
		const m = (n * xySum - xSum * ySum) / (n * xSquaredSum - xSum ** 2);
		const b = (ySum - m * xSum) / n;
		const yhat = data.map((d) => m * d.index + b);
		const ssres = data.reduce(
			(acc, d, i) => acc + (d.scatterY - yhat[i]) ** 2,
			0
		);
		const sstot = data.reduce(
			(acc, d) => acc + (d.scatterY - ySum / n) ** 2,
			0
		);
		const r2 = 1 - ssres / sstot;
		return [m, b, r2];
	};

	useEffect(() => {
		const CustomScatter = ({ cx, cy, r }) => {
			if (cx === null || cy === null || r === null) {
				return null;
			}

			return <circle cx={cx} cy={cy} r={r} fill="#02826B" />;
		};

		if ((chartData.length, linesData.length)) {
			const scatterplotData = [];
			const minXaxisValue = 1.5;
			for (let i = 0; i < chartData.length; i++) {
				const row = {};

				row["index"] =
					chartData[i][0] !== "" ? parseFloat(chartData[i][0]) : null;
				row["scatterY"] =
					chartData[i][1] !== "" ? parseFloat(chartData[i][1]) : null;
				if (row["index"] < minXaxisValue) {
					continue;
				}
				scatterplotData.push(row);
			}
			const [m, b, rSquared] = calculateDataForTrendline(scatterplotData);

			for (let i = 1.5; i <= 6.5; i += 0.1) {
				scatterplotData.push({ index: i, trendlineY: m * i + b });
			}

			for (let i = 0; i < linesData.length; i++) {
				const row = {};
				row["index"] =
					linesData[i][0] !== "" ? parseFloat(linesData[i][0]) : null;
				row["line1Y"] =
					linesData[i][2] !== "" ? parseFloat(linesData[i][2]) : null;
				row["line2Y"] =
					linesData[i][3] !== "" ? parseFloat(linesData[i][3]) : null;
				scatterplotData.push(row);
			}

			setChart(
				<ResponsiveContainer width="100%" height={450}>
					<ComposedChart
						data={scatterplotData}
						margin={{ bottom: 20 }}
					>
						<Tooltip />
						<XAxis
							dataKey="index"
							type="number"
							domain={[1.5, 6.5]}
							ticks={[
								1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5,
							]}
						>
							<Label
								value="Domestic Abuse"
								position="insideLeft"
								dy={20}
							/>
						</XAxis>

						<YAxis
							type="number"
							domain={[4, 8]}
							ticks={[4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8]}
							tick={{ dx: -10 }}
						>
							<Label
								value="Total CLA"
								position="insideBottom"
								angle={-90}
								dx={-18}
								dy={-30}
							/>
						</YAxis>
						<Scatter
							name="scatterY"
							dataKey="scatterY"
							shape={<CustomScatter r={2} />}
						/>
						<Line
							dataKey="trendlineY"
							dot={false}
							type="linear"
							stroke="#3E7F64"
							fill="#3E7F64"
							strokeDasharray="2 2"
						/>
						<text
							x={"80%"}
							y={"10%"}
							fill="#595959"
							fontSize={10}
							fontWeight="bold"
						>
							<tspan dy="+0.8ex">R</tspan>
							<tspan fontSize={8} dy="-0.8ex">
								2
							</tspan>
							<tspan dy="+0.8ex">
								{"= "}
								{rSquared.toFixed(4)}
							</tspan>
						</text>
						<Line
							dataKey="line1Y"
							dot={false}
							type="linear"
							stroke="#ED7D31"
							fill="#ED7D31"
						/>
						<Line
							dataKey="line2Y"
							dot={false}
							type="linear"
							stroke="#A5A5A5"
							fill="#A5A5A5"
						/>
					</ComposedChart>
				</ResponsiveContainer>
			);
		}
	}, [chartData, linesData]);
	setInterval(() => {
		sessionInfo && keepSessionAlive(sessionInfo, sheet);
	}, 5 * 60 * 1000); // Send the request every 5 minutes

	return (
		<div className="page-content" id="riskfactoranalysis-body">
			{sessionInfo ? (
				<div>
					<Typography variant="h4" style={{ marginBottom: 30 }}>
						Risk Factor Analysis
					</Typography>
					<Typography variant="body1" style={{ marginBottom: 30 }}>
						Use this dashboard to explore the projected number of
						CLA in Wales, based on a model considering the incidence
						of risk factors in each local authority. The dashboard
						also allows to estimate how the number of CLA will
						change if the prevalence of risk factors changes.
					</Typography>
					<div className="multiple-selects-container">
						<div className="select-container">
							<div className="select-text">
								Select the risk factor
							</div>
							<FormControl
								className="selection-field"
								variant="standard"
							>
								<InputLabel id="risk-factor-selection">
									Risk factor
								</InputLabel>
								<Select
									labelId="risk-factor-selection-options"
									id="risk-factor-select"
									onChange={(e) => {
										setValue(
											sessionInfo,
											sheet,
											6,
											1,
											e.target.value
										);
										setChartTitle(
											"Relationship between Total CLA and " +
												e.target.value
										);
										getData();
										if (!inputFilled[0]) {
											let inputFilledTemp = [
												...inputFilled,
											];
											inputFilledTemp[0] = true;
											setInputFilled(inputFilledTemp);
										}
									}}
									label="Risk factor"
									defaultValue={""}
								>
									{riskFactoroptions.map((option) => (
										<MenuItem value={option} key={option}>
											{" "}
											{option}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									Select the risk factor you are interested
									in. The dashboard will visualise the
									correlation between the chosen risk factor
									and the number of CLA.
								</FormHelperText>
							</FormControl>
						</div>
					</div>
					<div className="multiple-selects-container">
						<div className="select-container">
							<div className="select-text">
								Select change in risk factor(s)
							</div>
							<FormControl variant="standard">
								<InputLabel id="deprivation-select-label">
									Deprivation
								</InputLabel>
								<Select
									labelId="deprivation-select-label"
									id="deprivation-select"
									onChange={(e) => {
										setValue(
											sessionInfo,
											sheet,
											6,
											10,
											e.target.value / 100
										);
										getData();
										if (!inputFilled[1]) {
											let inputFilledTemp = [
												...inputFilled,
											];
											inputFilledTemp[1] = true;
											setInputFilled(inputFilledTemp);
										}
									}}
									label="Deprivation"
									defaultValue={""}
								>
									{percentages.map((option) => (
										<MenuItem value={option} key={option}>
											{option.toString() + " %"}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
						<div className="select-container">
							<div
								className="select-text"
								style={{ visibility: "hidden" }}
							>
								Select change in risk factor(s)
							</div>
							<FormControl variant="standard">
								<InputLabel id="parental-mental-health-select-label">
									Parental mental health
								</InputLabel>
								<Select
									labelId="parental-mental-health-select-label"
									id="parental-mental-health-select"
									onChange={(e) => {
										setValue(
											sessionInfo,
											sheet,
											6,
											11,
											e.target.value / 100
										);
										getData();
										if (!inputFilled[2]) {
											let inputFilledTemp = [
												...inputFilled,
											];
											inputFilledTemp[2] = true;
											setInputFilled(inputFilledTemp);
										}
									}}
									label="Parental mental health"
									defaultValue={""}
								>
									{percentages.map((option) => (
										<MenuItem value={option} key={option}>
											{option.toString() + " %"}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
						<div className="select-container">
							<div
								className="select-text"
								style={{ visibility: "hidden" }}
							>
								Select change in risk factor(s)
							</div>
							<FormControl variant="standard">
								<InputLabel id="domestic-abuse-select-label">
									Domestic abuse
								</InputLabel>
								<Select
									labelId="domestic-abuse-select-label"
									id="domestic-abuse-select"
									onChange={(e) => {
										setValue(
											sessionInfo,
											sheet,
											6,
											12,
											e.target.value / 100
										);
										getData();
										if (!inputFilled[3]) {
											let inputFilledTemp = [
												...inputFilled,
											];
											inputFilledTemp[3] = true;
											setInputFilled(inputFilledTemp);
										}
									}}
									label="Domestic abuse"
									defaultValue={""}
								>
									{percentages.map((option) => (
										<MenuItem value={option} key={option}>
											{option.toString() + " %"}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
					</div>
					<FormHelperText style={{ marginBottom: "30px" }}>
						Select the changes in risk factors. The dashboard will
						project the number of CLA based on changes in risk
						factors and will allow you to compare the projected
						number with the current number of CLA in Wales. You can
						change one, two or three risk factors. You can also
						choose not to change any risk factor.
					</FormHelperText>
					{inputFilled.every((element) => element === true) ? (
						datagridColumns.length && datagridRows.length ? (
							<>
								<div className="outputs-data-container">
									<div className="table-container">
										<h3 className="component-title">
											{tableTitle}
										</h3>
										<DataGrid
											rows={datagridRows}
											columns={datagridColumns}
											disableRowSelectionOnClick
											disableColumnMenu
											disableColumnSelector
											disableVirtualization
											hideFooter={true}
											autoHeight={true}
											density={"compact"}
										/>
									</div>
									<div className="chart-container">
										<h3 className="component-title">
											{chartTitle}
										</h3>
										{chart}
										<div className="table-footer-text">
											All variables have been natural
											logarithm-transformed for ease of
											interpretation. When we take the
											natural logarithm of the independent
											and dependent variables in a
											regression, we can then interpret a
											1% increase in the independent
											variable as correlated with an X%
											increase in the dependent variable,
											where X is the regression
											coefficient.
										</div>
									</div>
								</div>
							</>
						) : (
							<div className="loader" />
						)
					) : (
						<div className="no-data-text">
							Start entering data to get the project outputs
						</div>
					)}
				</div>
			) : (
				<div className="loader" />
			)}
		</div>
	);
}
export default RiskFactorAnalysis;
